// File and rank labels around the board's edge, on hover.
//
// A grown board has no border to print them on and no fixed extent to learn,
// so they cannot be permanent furniture: at eight-by-eight they are pleasant
// and at forty squares across they are forty numbers competing with the
// pieces for the same margin. Hovering the board is the moment you want to
// name a square and the moment nothing else is being asked of the screen, so
// that is when they come.
//
// They anchor exactly like the piece badges — projected from the renderer's
// own frame, so they hold still against a moving camera. Not `BoardAnchored`:
// that primitive takes one square and this projects every label in the board's
// margin at once, off one sweep and one `setState`.

import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

import type { BoardFacts } from "src/api";
import type { BoardCanvasHandle } from "./BoardCanvas";
// The one engine import left here: which squares get a file or rank label is
// a model (`gridLabels`, with its own tests), and the contract has no field
// for it yet — `BoardFacts.labels` would be the honest home.
import { type GridLabel, gridLabels } from "src/engine/session/gridLabels";
import { motion, styled, useOverlayClip } from "./kit";

interface Props {
  /** The board on screen; only its squares are read. */
  facts: BoardFacts;
  board: RefObject<BoardCanvasHandle | null>;
  /** True while the pointer is over the board. */
  shown: boolean;
}

type Points = Record<number, { x: number; y: number }>;

/**
 * The layer. Inert, and clipped to what the chrome leaves free.
 *
 * Slow out, quick in: the labels should feel like they were always there
 * rather than announce themselves, and they should not linger over a board you
 * have gone back to playing on. Two durations, so the transition is written
 * here rather than through `motion()` — which emits one.
 */
const Layer = styled("div")(({ theme }) => ({
  position: "fixed",
  inset: 0,
  pointerEvents: "none",
  zIndex: theme.kit.z.boardOverlay,
  ...motion([], ["opacity"], theme.kit.motion.duration.fast)
}));

/**
 * One label, in the display face.
 *
 * The display role is the numerals' own: a rank is a number and a file is a
 * letter beside one, so they line up in the same voice the readouts count in.
 * The shadow is the page's ground under a lit board — enough to read a
 * character against a bright tile without drawing a plate for it.
 */
const Label = styled("div")(({ theme }) => ({
  position: "absolute",
  transform: "translate(-50%, -50%)",
  fontFamily: theme.kit.font.display,
  fontVariantNumeric: "tabular-nums",
  fontSize: 13,
  fontWeight: 600,
  color: theme.kit.color.inkDim,
  textShadow: `0 1px 3px ${theme.kit.color.shadow}`,
  userSelect: "none"
}));

export function BoardLabels({ facts, board, shown }: Props) {
  // What the chrome covers, from the inset registry — the one geometry source.
  const clip = useOverlayClip();
  const [labels, setLabels] = useState<GridLabel[]>([]);
  const [points, setPoints] = useState<Points>({});
  const latest = useRef(labels);

  // Recomputed when the facts do — once per shown board, since they are
  // memoised on it — and only over the squares that are ground: a structure's
  // floor is drawn as a cell and is not one.
  useEffect(() => {
    const cells: number[] = [];
    for (const [square, at] of facts.squares) if (at.cell === "board") cells.push(square);
    const next = gridLabels(cells);
    latest.current = next;
    setLabels(next);
  }, [facts]);

  const project = useCallback(() => {
    const centre = board.current?.projectCentre;
    if (!centre) return;
    const next: Points = {};
    for (const label of latest.current) next[label.square] = centre(label.square);
    setPoints((previous) => (samePoints(previous, next) ? previous : next));
  }, [board]);

  // Only while they are on screen. Projecting a hidden label every frame is
  // work for nobody, and on a big board there are a lot of them.
  useEffect(() => {
    if (!shown) return;
    project();
    return board.current?.onFrame(project);
  }, [board, project, shown, labels]);

  return (
    <Layer
      data-testid="grid-labels"
      // Only over the free area: a rank label for a square behind the hotbar
      // draws itself neatly on top of it otherwise. In `style`, not `sx`,
      // because it changes whenever a piece of chrome moves and emotion mints
      // a permanent class per distinct `sx` value.
      style={{
        clipPath: clip,
        opacity: shown ? 1 : 0,
        transitionDuration: shown ? "140ms" : "320ms"
      }}
    >
      {labels.map((label) => {
        const point = points[label.square];
        if (!point) return null;
        return (
          <Label
            // Keyed by axis *and* square: on a ragged board a file's label and
            // a rank's label can land on the same square — the corner outside
            // both lines — and React then drops one of them.
            key={`${label.axis}-${label.square}`}
            data-testid={`grid-label-${label.axis}-${label.text}`}
            // In `style`, not `sx`: these move with the camera, and an `sx`
            // value that changes mints a permanent generated class every time
            // it does. See the note in `PieceOverlays`.
            style={{ left: point.x, top: point.y }}
          >
            {label.text}
          </Label>
        );
      })}
    </Layer>
  );
}

function samePoints(a: Points, b: Points): boolean {
  const keys = Object.keys(b);
  if (Object.keys(a).length !== keys.length) return false;
  for (const key of keys) {
    const previous = a[Number(key)];
    const next = b[Number(key)];
    if (!previous || previous.x !== next.x || previous.y !== next.y) return false;
  }
  return true;
}
